import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorMessage } from '../../services/api'
import { useAuthStore } from '../../context/auth'
import { Badge, Button, ErrorText, Field, Modal, Spinner, Textarea } from '../../components/ui'
import {
  APPOINTMENT_STATUS_COLORS,
  APPOINTMENT_STATUS_LABELS,
  clp,
  formatDateTime,
  formatRut,
  insuranceLabel,
} from '../../utils/format'
import type { Appointment, AppointmentStatus } from '../../types/api'
import { Icon } from '../../components/ui/icons'
import { TransitControls } from './TransitControls'
import { LogisticsPanel } from './LogisticsPanel'

/**
 * Acciones administrativas por estado. Las marcas del día (Llegó / Pasó a box /
 * Atendido), el llamado a box y la no atención viven en `TransitControls` para
 * no duplicar botones que hacen lo mismo.
 */
const ACTIONS: Partial<Record<AppointmentStatus, Array<{ to: AppointmentStatus; label: string; variant: 'primary' | 'secondary' | 'danger' | 'cta' }>>> = {
  REQUESTED: [
    { to: 'SCHEDULED', label: 'Agendar', variant: 'primary' },
    { to: 'CANCELLED', label: 'Anular', variant: 'danger' },
  ],
  SCHEDULED: [
    { to: 'CONFIRMED', label: 'Confirmar', variant: 'primary' },
    { to: 'CANCELLED', label: 'Anular', variant: 'danger' },
  ],
  CONFIRMED: [{ to: 'CANCELLED', label: 'Anular', variant: 'danger' }],
  ARRIVED: [{ to: 'CANCELLED', label: 'Anular', variant: 'danger' }],
  ATTENDED: [{ to: 'CLOSED', label: 'Cerrar atención', variant: 'primary' }],
}

export function AppointmentDetailModal({ appointmentId, onClose }: { appointmentId: string; onClose: () => void }) {
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const [error, setError] = useState('')
  const [cancelReason, setCancelReason] = useState('')
  const [showCancel, setShowCancel] = useState(false)

  const { data: appointment, isLoading } = useQuery({
    queryKey: ['appointment', appointmentId],
    queryFn: async () => (await api.get<Appointment>(`/appointments/${appointmentId}`)).data,
  })

  const transition = useMutation({
    mutationFn: async ({ status, reason }: { status: AppointmentStatus; reason?: string }) =>
      (await api.post(`/appointments/${appointmentId}/transition`, { status, reason })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
      queryClient.invalidateQueries({ queryKey: ['appointment', appointmentId] })
      queryClient.invalidateQueries({ queryKey: ['charges'] })
      setShowCancel(false)
      setError('')
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  if (isLoading || !appointment) {
    return (
      <Modal title="Cita" onClose={onClose}>
        <Spinner />
      </Modal>
    )
  }

  const isDoctor = user?.role === 'DOCTOR'
  const actions = (ACTIONS[appointment.status] ?? []).filter((action) => {
    if (!isDoctor) return true
    return ['CLOSED'].includes(action.to) // médico: solo estados clínicos
  })

  return (
    <Modal title="Detalle de la cita" onClose={onClose} wide>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Badge className={APPOINTMENT_STATUS_COLORS[appointment.status]}>
            {APPOINTMENT_STATUS_LABELS[appointment.status]}
          </Badge>
          <p className="flex items-center gap-2 text-sm text-gray-500">
            {appointment.isOverbooking && <Badge className="bg-amber-100 text-amber-800">Sobrecupo</Badge>}
            {appointment.channel === 'WEB' && (
              <Badge className="bg-brand-100 text-brand-700">Reservada en línea</Badge>
            )}
            {formatDateTime(appointment.startsAt)}
          </p>
        </div>

        <div className="grid gap-3 text-sm sm:grid-cols-2">
          <div className="rounded-lg bg-soft p-3">
            <p className="font-semibold text-ink">
              {appointment.patient.firstName} {appointment.patient.lastName}
            </p>
            <p className="text-gray-600">{formatRut(appointment.patient.rut)} · {insuranceLabel(appointment.patient)}</p>
            {appointment.patient.phone && <p className="flex items-center gap-1.5 text-gray-600"><Icon name="mobile" className="h-4 w-4 text-steel-500" />{appointment.patient.phone}</p>}
          </div>
          <div className="rounded-lg bg-soft p-3">
            <p className="font-semibold text-ink">{appointment.service.name}</p>
            <p className="text-gray-600">
              {appointment.professional.firstName} {appointment.professional.lastName} · {appointment.location.name}
              {appointment.box ? ` · ${appointment.box.name}` : ''}
            </p>
            <p className="text-gray-600">{clp(appointment.service.basePrice)}</p>
          </div>
        </div>

        {appointment.preOccupationalOrder && (
          <p className="flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <Icon name="preoccupational" className="h-4 w-4 shrink-0" />Orden preocupacional{appointment.preOccupationalOrder.company ? ` — ${appointment.preOccupationalOrder.company.businessName}` : ''}
          </p>
        )}

        {appointment.charge && (
          <p className="flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-2 text-sm">
            <Icon name="cashier" className="h-4 w-4 shrink-0 text-emerald-700" />Cargo asociado: {clp(appointment.charge.amount)} —{' '}
            <strong>{appointment.charge.status === 'PAID' ? 'Pagado' : appointment.charge.status === 'PENDING' ? 'Pendiente de pago' : appointment.charge.status}</strong>
          </p>
        )}

        {appointment.cancellationReason && (
          <p className="text-sm text-red-600">Motivo de anulación: {appointment.cancellationReason}</p>
        )}

        {/* La ficha se abre en su propia pantalla: es donde vive el trabajo clínico. */}
        {appointment.encounter && ['DOCTOR', 'ADMIN'].includes(user?.role ?? '') && (
          <Link
            to={`/app/atencion/${appointment.encounter.id}`}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            <Icon name="checkup" className="h-4 w-4" />
            {appointment.encounter.status === 'CLOSED' ? 'Ver ficha de la atención' : 'Abrir ficha de la atención'}
          </Link>
        )}

        {/* Domicilio o telemedicina: dirección de visita / enlace (H7). */}
        <LogisticsPanel appointment={appointment} />

        {/* Tránsito del paciente en la sede: marcas, llamado y no atención. */}
        <div className="rounded-lg border border-steel-100 bg-white p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-steel-500">Tránsito del paciente</p>
          <TransitControls appointment={appointment} />
        </div>

        {!showCancel && actions.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {actions.map((action) =>
              action.to === 'CANCELLED' ? (
                <Button key={action.to} variant="danger" onClick={() => setShowCancel(true)}>
                  {action.label}
                </Button>
              ) : (
                <Button
                  key={action.to}
                  variant={action.variant}
                  disabled={transition.isPending}
                  onClick={() => transition.mutate({ status: action.to, reason: action.to === 'NO_SHOW' ? 'Inasistencia registrada' : undefined })}
                >
                  {action.label}
                </Button>
              ),
            )}
          </div>
        )}

        {showCancel && (
          <div className="space-y-2 rounded-lg border border-red-200 bg-red-50 p-3">
            <Field label="Motivo de anulación" hint={`Ventana de anulación: ${appointment.service.cancellationWindowHours} h antes de la cita.`}>
              <Textarea rows={2} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
            </Field>
            <div className="flex gap-2">
              <Button
                variant="danger"
                disabled={transition.isPending}
                onClick={() => transition.mutate({ status: 'CANCELLED', reason: cancelReason || undefined })}
              >
                Confirmar anulación
              </Button>
              <Button variant="ghost" onClick={() => setShowCancel(false)}>Volver</Button>
            </div>
          </div>
        )}

        {(appointment.events?.length ?? 0) > 0 && (
          <details className="text-sm">
            <summary className="cursor-pointer font-medium text-steel-600">Historial de estados</summary>
            <ul className="mt-2 space-y-1 text-gray-600">
              {appointment.events!.map((e) => (
                <li key={e.id}>
                  {formatDateTime(e.createdAt)} — {e.fromStatus ? `${APPOINTMENT_STATUS_LABELS[e.fromStatus]} → ` : ''}
                  {APPOINTMENT_STATUS_LABELS[e.toStatus]}
                  {e.reason ? ` (${e.reason})` : ''}
                </li>
              ))}
            </ul>
          </details>
        )}

        <ErrorText>{error}</ErrorText>
      </div>
    </Modal>
  )
}
